import { Button } from '../../components/ui/button';
import { AlertTriangle } from 'lucide-react';

export const FeatureFlagsTab = ({ featureFlags, selectedOrg, setSelectedOrg, onToggle, onReset }) => {
  if (!featureFlags) return null;

  const categories = {};
  Object.entries(featureFlags.definitions || {}).forEach(([id, def]) => {
    const cat = def.category || 'other';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push({ id, ...def });
  });
  const globalFlags = featureFlags.global_flags || {};
  const orgOverride = selectedOrg !== 'global'
    ? (featureFlags.org_overrides || []).find(o => o.org_id === selectedOrg)?.flags || {}
    : {};

  const getEffective = (flagId, defaultVal) => {
    if (selectedOrg !== 'global' && flagId in orgOverride) return orgOverride[flagId];
    if (flagId in globalFlags) return globalFlags[flagId];
    return defaultVal;
  };

  const catLabels = {
    core: 'Core Features', analysis: 'Analysis Tools', tools: 'Advanced Tools',
    compliance: 'Compliance', ai: 'AI Features', integration: 'Integrations',
    collaboration: 'Collaboration'
  };

  return (
    <div className="space-y-6" data-testid="feature-flags-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">Feature Flags</h3>
        <div className="flex items-center gap-3">
          <select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="feature-flag-scope-select"
          >
            <option value="global">Global (All Orgs)</option>
            {(featureFlags.organizations || []).map(org => (
              <option key={org.org_id} value={org.org_id}>{org.name}</option>
            ))}
          </select>
          {selectedOrg !== 'global' && (
            <Button variant="outline" size="sm" onClick={() => onReset(selectedOrg)} data-testid="reset-org-flags-btn">
              Reset to Global
            </Button>
          )}
        </div>
      </div>

      {selectedOrg !== 'global' && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Showing overrides for <strong>{(featureFlags.organizations || []).find(o => o.org_id === selectedOrg)?.name}</strong>. Unset flags inherit from global.</span>
        </div>
      )}

      {Object.entries(categories).map(([cat, flags]) => (
        <div key={cat} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50/50">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">{catLabels[cat] || cat}</h4>
          </div>
          <div className="divide-y divide-slate-100">
            {flags.map(flag => {
              const isEnabled = getEffective(flag.id, flag.default);
              const isOverridden = selectedOrg !== 'global' ? flag.id in orgOverride : flag.id in globalFlags;
              return (
                <div key={flag.id} className="flex items-center justify-between p-4 hover:bg-slate-50/50" data-testid={`feature-flag-${flag.id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{flag.label}</p>
                    <p className="text-xs text-slate-500">{flag.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {isOverridden && (
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">modified</span>
                    )}
                    <button
                      onClick={() => onToggle(flag.id, isEnabled)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${isEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      data-testid={`feature-flag-toggle-${flag.id}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-6' : ''}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
